// src/components/Reminders.jsx
import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import { Bell, Calendar, User, Edit, Trash2, Plus, ExternalLink } from 'lucide-react';
import EditReminder from './EditReminder';
import AddReminder from './AddReminder';
import Loading from '../utils/Loading';
import Breadcrumb from '../utils/Breadcrumb';
import IndividualSidebar from '../individuals/IndividualSidebar';
import { getFirstEmail, getAllEmails } from '../../utils/emailHelpers';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const Reminders = () => {
  const API = useContext(ApiContext);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReminder, setEditingReminder] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'completed'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'individual'
  const [dateSort, setDateSort] = useState('asc'); // 'asc', 'desc'
  const [selectedReminders, setSelectedReminders] = useState([]); // IDs des rappels sélectionnés
  const [showIndividualSidebar, setShowIndividualSidebar] = useState(false);
  const [selectedIndividual, setSelectedIndividual] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);

  // Récupérer tous les rappels (uniquement ceux non associés à des positions)
  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/reminders', {
        params: { excludePositions: 'true' } // Exclure les reminders liés aux positions
      });
      if (response.data.success) {
        setReminders(response.data.reminders);
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
      toast.error('Erreur lors du chargement des tâches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  // Gérer la mise à jour d'un rappel
  const handleReminderUpdate = (updatedReminder, deletedId = null) => {
    if (deletedId) {
      // Suppression
      setReminders(prev => prev.filter(r => r._id !== deletedId));
    } else if (updatedReminder) {
      // Mise à jour
      setReminders(prev => 
        prev.map(r => r._id === updatedReminder._id ? updatedReminder : r)
      );
    }
  };

  // Handle marking a reminder as done
  const toggleReminderDone = async (reminder, event) => {
    // Empêcher la propagation de l'événement
    event.stopPropagation();
    
    try {
      await API.put(`/api/reminders/${reminder._id}`, {
        done: !reminder.done
      });
      
      // Update local state
      setReminders(prevReminders => 
        prevReminders.map(r => 
          r._id === reminder._id ? { ...r, done: !r.done } : r
        )
      );
      
      toast.success(`Tâche ${!reminder.done ? 'effectuée' : 'en cours'}`);
    } catch (err) {
      toast.error('Failed to update reminder');
      console.error('Error updating reminder:', err);
    }
  };

  // Sélection des rappels
  const toggleReminderSelection = (reminderId) => {
    setSelectedReminders(prev => 
      prev.includes(reminderId) 
        ? prev.filter(id => id !== reminderId)
        : [...prev, reminderId]
    );
  };

  const toggleAllReminders = () => {
    const allCurrentIds = filteredAndSortedReminders.map(r => r._id);
    setSelectedReminders(prev => 
      prev.length === allCurrentIds.length 
        ? [] 
        : allCurrentIds
    );
  };

  // Supprimer plusieurs rappels sélectionnés
  const deleteSelectedReminders = async () => {
    if (selectedReminders.length === 0) return;

    setShowBulkDeleteDialog(false);

    try {
      await Promise.all(
        selectedReminders.map(id => API.delete(`/api/reminders/${id}`))
      );
      
      setReminders(prev => prev.filter(r => !selectedReminders.includes(r._id)));
      setSelectedReminders([]);
      toast.success(`${selectedReminders.length} tâche(s) supprimée(s) avec succès`);
    } catch (error) {
      console.error('Error deleting reminders:', error);
      toast.error('Erreur lors de la suppression des tâches');
    }
  };

  // Marquer plusieurs rappels comme terminés
  const markSelectedAsComplete = async (completed = true) => {
    if (selectedReminders.length === 0) return;

    try {
      await Promise.all(
        selectedReminders.map(id => 
          API.put(`/api/reminders/${id}`, { done: completed })
        )
      );
      
      setReminders(prev => 
        prev.map(r => 
          selectedReminders.includes(r._id) 
            ? { ...r, done: completed }
            : r
        )
      );
      
      setSelectedReminders([]);
      toast.success(`${selectedReminders.length} tâche(s) marquée(s) comme ${completed ? 'terminée(s)' : 'non terminée(s)'}`);
    } catch (error) {
      console.error('Error updating reminders:', error);
      toast.error('Erreur lors de la mise à jour des tâches');
    }
  };

  const deleteReminder = async () => {
    if (!reminderToDelete) return;

    const reminderId = reminderToDelete;
    setShowDeleteDialog(false);
    setReminderToDelete(null);

    try {
      const response = await API.delete(`/api/reminders/${reminderId}`);
      if (response.data.success) {
        setReminders(prev => prev.filter(r => r._id !== reminderId));
        toast.success('Tâche supprimée avec succès');
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Erreur lors de la suppression de la tâche');
    }
  };

  // Ouvrir la sidebar de l'individual
  const openIndividualSidebar = async (individual) => {
    if (individual && individual._id) {
      try {
        // Récupérer les détails complets de l'individual
        const response = await API.get(`/api/individuals/${individual._id}`);
        if (response.data) {
          setSelectedIndividual(response.data);
          setShowIndividualSidebar(true);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des détails:', error);
        toast.error('Erreur lors du chargement des informations');
        // Fallback: utiliser les données partielles
        setSelectedIndividual(individual);
        setShowIndividualSidebar(true);
      }
    }
  };

  // Fermer la sidebar de l'individual
  const closeIndividualSidebar = () => {
    setShowIndividualSidebar(false);
    setSelectedIndividual(null);
  };

  // ✅ CORRECTION: Fonction pour changer le tri par date
  const toggleDateSort = () => {
    const newDateSort = dateSort === 'asc' ? 'desc' : 'asc';
    setDateSort(newDateSort);
    // S'assurer que le tri est bien sur 'date'
    if (sortBy !== 'date') {
      setSortBy('date');
    }
  };

  // ✅ CORRECTION: Filtrer et trier les rappels avec une logique de tri corrigée
  const filteredAndSortedReminders = React.useMemo(() => {
    let filtered = reminders;

    // Appliquer le filtre
    switch (filter) {
      case 'pending':
        filtered = filtered.filter(r => !r.done && new Date(r.reminderDate) >= new Date());
        break;
      case 'completed':
        filtered = filtered.filter(r => r.done);
        break;
      case 'overdue':
        filtered = filtered.filter(r => !r.done && new Date(r.reminderDate) < new Date());
        break;
      default:
        // 'all' - ne pas filtrer
        break;
    }

    // ✅ CORRECTION: Appliquer le tri avec une logique améliorée
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.reminderDate);
        const dateB = new Date(b.reminderDate);
        
        // Tri ascendant ou descendant selon dateSort
        if (dateSort === 'asc') {
          return dateA.getTime() - dateB.getTime();
        } else {
          return dateB.getTime() - dateA.getTime();
        }
      } else if (sortBy === 'individual') {
        const nameA = a.individual?.Name || '';
        const nameB = b.individual?.Name || '';
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

    return filtered;
  }, [reminders, filter, sortBy, dateSort]); // ✅ Ajouter dateSort aux dépendances

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const timeOptions = { 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    const dateOptions = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    };

    const timeStr = date.toLocaleTimeString(undefined, timeOptions);
    const dateStr = date.toLocaleDateString(undefined, dateOptions);

    if (diffDays === 0) {
      return `Aujourd'hui ${timeStr}`;
    } else if (diffDays === 1) {
      return `Demain ${timeStr}`;
    } else if (diffDays === -1) {
      return `Hier ${timeStr}`;
    } else {
      return `${dateStr} ${timeStr}`;
    }
  };

  // Obtenir la classe de couleur selon le statut
  const getStatusBadge = (reminder) => {
    if (reminder.done) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ✓ Terminée
        </span>
      );
    }
    
    const now = new Date();
    const reminderDate = new Date(reminder.reminderDate);
    
    if (reminderDate < now) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          ⚠ En retard
        </span>
      );
    } else if (reminderDate.getTime() - now.getTime() < 24 * 60 * 60 * 1000) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          ⚠ Urgent
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          ⏱ En attente
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-100">
        <Loading message="Chargement des tâches..." fullHeight={true} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-16 p-4 sm:p-6 lg:p-10">
      {/* Breadcrumb */}
      <div className='pt-10'>
        <Breadcrumb items={[
        { label: 'Dashboard', href: '/app', icon: 'bi-house' },
        { label: 'Tâches', href: null, icon: 'bi-check-circle', isActive: true }
      ]} />
      </div>
      
      <div className=" mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex items-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 m-0">
              Tâches ({filteredAndSortedReminders.length})
            </h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors self-start sm:self-auto"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Nouvelle tâche</span>
            <span className="sm:hidden">Nouvelle</span>
          </button>
        </div>

        {/* Filtres sous forme de tabs */}
        <div className="bg-white rounded-lg  border border-slate-200 mb-6">
          <div className=" border-slate-200">
            <nav className="-mb-px flex flex-wrap gap-x-4 sm:gap-x-8 px-4 sm:px-6" aria-label="Tabs">
              {[
                { key: 'all', label: 'Toutes' },
                { key: 'pending', label: 'En attente' },
                { key: 'overdue', label: 'En retard' },
                { key: 'completed', label: 'Terminées' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setFilter(tab.key);
                    setSelectedReminders([]); // Reset selection when changing filter
                  }}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                    filter === tab.key
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {tab.label}                  
                </button>
              ))}
            </nav>
          </div>

          {/* Actions de masse et tri */}
          

              {/* Actions de masse */}
              <div className="flex  sm:flex-row sm:items-center gap-3">
                {selectedReminders.length > 0 && (
                  <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 rounded-b-lg flex flex-col border-t sm:flex-row sm:items-center sm:justify-between w-full border-slate-200">
                    <span className="text-xs sm:text-sm text-slate-700 font-medium">
                      {selectedReminders.length} sélectionné(s)
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => markSelectedAsComplete(true)}
                        className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                      >
                        <span className="hidden sm:inline">Marquer terminé</span>
                        <span className="sm:hidden">✓</span>
                      </button>
                      <button
                        onClick={() => markSelectedAsComplete(false)}
                        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        <span className="hidden sm:inline">En cours</span>
                        <span className="sm:hidden">⏳</span>
                      </button>
                      <button
                        onClick={() => setShowBulkDeleteDialog(true)}
                        className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                      >
                        <span className="hidden sm:inline">Supprimer</span>
                        <span className="sm:hidden">🗑</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
        </div>

        {/* Tableau des rappels */}
        {filteredAndSortedReminders.length === 0 ? (
          <div className="bg-white rounded-lg  border border-slate-200 p-12 text-center">
            <Bell size={64} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-medium text-slate-600 mb-2">
              Aucune tâche
            </h3>
            <p className="text-slate-500 mb-2">
              {filter === 'all'
                ? 'Vous n\'avez aucune tâche pour le moment.'
                : `Aucune tâche ${
                    filter === 'pending' ? 'en attente' :
                    filter === 'overdue' ? 'en retard' :
                    filter === 'completed' ? 'terminée' : ''
                  }.`
              }
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Créer ma première tâche
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider ">
                      <div className="flex items-center space-x-2">

                      <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                          checked={selectedReminders.length === filteredAndSortedReminders.length && filteredAndSortedReminders.length > 0}
                          onChange={toggleAllReminders}
                        />
                      </div>
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Contact
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="hidden sm:inline">Date & Heure</span>
                        <span className="sm:hidden">Date</span>
                        <button
                          onClick={toggleDateSort}
                          className="ml-1 sm:ml-2 p-1 hover:bg-gray-200 rounded transition-colors"
                          title={`Trier par date ${dateSort === 'asc' ? 'décroissante' : 'croissante'}`}
                        >
                          {dateSort === 'asc' ? (
                            <i className="bi bi-arrow-up text-indigo-600 text-xs"></i>
                          ) : (
                            <i className="bi bi-arrow-down text-indigo-600 text-xs"></i>
                          )}
                        </button>
                      </div>
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Tâche
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                      Statut
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredAndSortedReminders.map((reminder) => (
                    <tr key={reminder._id} className="hover:bg-gray-50 transition-colors">
                      {/* Colonne checkbox */}
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                            checked={selectedReminders.includes(reminder._id)}
                            onChange={() => toggleReminderSelection(reminder._id)}
                          />
                         
                        </div>
                      </td>
                      {/* Colonne Statut (icône) */}
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                             <button
                            onClick={(e) => toggleReminderDone(reminder, e)}
                            className={`p-1 rounded-full transition-colors ${
                              reminder.done 
                                ? 'text-green-600 hover:bg-green-100' 
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={reminder.done ? 'Marquer comme non terminée' : 'Marquer comme terminée'}
                          >
                            <i className={`bi ${reminder.done ? 'bi-check-circle-fill' : 'bi-check-circle'} text-lg sm:text-xl`}></i>
                          </button>
                          </div>
                      </td>

                      {/* Colonne Contact */}
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        {reminder.individual ? (
                          <div className="flex items-center">
                            {reminder.individual.photo ? (
                              <img 
                                src={reminder.individual.photo} 
                                alt={reminder.individual.Name}
                                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full mr-2 sm:mr-3 object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-200 mr-2 sm:mr-3 flex items-center justify-center flex-shrink-0">
                                <User size={12} className="text-slate-500 sm:w-4 sm:h-4" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <button
                                onClick={() => openIndividualSidebar(reminder.individual)}
                                className="text-xs sm:text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline flex items-center truncate"
                              >
                                <span className="truncate max-w-[100px] sm:max-w-[150px]">
                                  {reminder.individual.Name}
                                </span>
                                <ExternalLink size={10} className="ml-1 flex-shrink-0 hidden sm:inline" />
                              </button>
                              {(getAllEmails(reminder.individual.professionalEmail).length > 0 || getAllEmails(reminder.individual.personalEmail).length > 0) && (
                                <div className="text-xs text-slate-500 truncate max-w-[100px] sm:max-w-[150px] hidden sm:block">
                                  {getFirstEmail(reminder.individual.professionalEmail) || getFirstEmail(reminder.individual.personalEmail)}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs sm:text-sm text-slate-500 italic">Aucun contact</span>
                        )}
                      </td>

                      {/* Colonne Date & Heure */}
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center text-xs sm:text-sm text-slate-900">
                          <Calendar size={12} className="mr-1 sm:mr-2 text-slate-400 flex-shrink-0" />
                          <span className="truncate">{formatDate(reminder.reminderDate)}</span>
                        </div>
                      </td>

                      {/* Colonne Tâche */}
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className={`text-xs sm:text-sm text-slate-900 ${reminder.done ? 'line-through opacity-60' : ''}`}>
                          <div className="max-w-[150px] sm:max-w-xs overflow-hidden">
                            <div className="truncate sm:hidden">
                              {reminder.text.length > 30 
                                ? `${reminder.text.substring(0, 30)}...` 
                                : reminder.text
                              }
                            </div>
                            <div className="hidden sm:block">
                              {reminder.text.length > 100 
                                ? `${reminder.text.substring(0, 100)}...` 
                                : reminder.text
                              }
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Colonne Statut - cachée sur mobile */}
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden md:table-cell">
                        {getStatusBadge(reminder)}
                      </td>

                      {/* Colonne Actions */}
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                          <button
                            onClick={() => setEditingReminder(reminder)}
                            className="p-1.5 sm:p-2 text-slate-400 hover:text-indigo-600 transition-colors rounded-full hover:bg-indigo-50"
                            title="Modifier la tâche"
                          >
                            <Edit size={14} className="sm:w-4 sm:h-4" />
                          </button>

                          <button
                            onClick={() => {
                              setReminderToDelete(reminder._id);
                              setShowDeleteDialog(true);
                            }}
                            className="p-1.5 sm:p-2 text-slate-400 hover:text-red-600 transition-colors rounded-full hover:bg-red-50"
                            title="Supprimer la tâche"
                          >
                            <Trash2 size={14} className="sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer du tableau avec statistiques */}
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500">
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                  <span>
                    {filteredAndSortedReminders.length} tâche(s) affichée(s)
                  </span>
                  {selectedReminders.length > 0 && (
                    <span className="text-indigo-600 font-medium">
                      {selectedReminders.length} sélectionné(s)
                    </span>
                  )}
                </div>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-1.5"></span>
                    {filteredAndSortedReminders.filter(r => !r.done).length} en attente
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                    {filteredAndSortedReminders.filter(r => r.done).length} terminée(s)
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1.5"></span>
                    {filteredAndSortedReminders.filter(r => !r.done && new Date(r.reminderDate) < new Date()).length} en retard
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal d'édition */}
        {editingReminder && (
          <EditReminder
            reminder={editingReminder}
            API={API}
            onClose={() => setEditingReminder(null)}
            onUpdate={handleReminderUpdate}
          />
        )}

        {/* Modal d'ajout */}
        {showAddModal && (
          <AddReminder
            individualId={null} // Null car on peut créer un rappel sans contact spécifique
            API={API}
            onClose={() => setShowAddModal(false)}
          />
        )}

        {/* Sidebar de l'individual */}
        {showIndividualSidebar && selectedIndividual && (
          <IndividualSidebar
            isOpen={showIndividualSidebar}
            onClose={closeIndividualSidebar}
            individual={selectedIndividual}
          />
        )}

        {/* Confirmation Dialog for Bulk Delete */}
        <ConfirmationDialog
          isOpen={showBulkDeleteDialog}
          onClose={() => setShowBulkDeleteDialog(false)}
          onConfirm={deleteSelectedReminders}
          title="Supprimer les tâches"
          message={`Êtes-vous sûr de vouloir supprimer ${selectedReminders.length} tâche(s) ? Cette action est irréversible.`}
          confirmText="Supprimer"
          cancelText="Annuler"
          isDangerous={true}
        />

        {/* Confirmation Dialog for Single Delete */}
        <ConfirmationDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setReminderToDelete(null);
          }}
          onConfirm={deleteReminder}
          title="Supprimer la tâche"
          message="Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          isDangerous={true}
        />
      </div>
    </div>
  );
};

export default Reminders;